import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { palletPath } from '../../app/routes'
import { CameraViewfinder } from '../../components/scanner/CameraViewfinder'
import { ScanSimulator } from '../../components/scanner/ScanSimulator'
import { useToast } from '../../components/feedback/toastContext'
import { useDeviceSettings } from '../../feedback/settings'
import { faRatio } from '../../lib/faNumbers'
import { useBarcodeScanner } from '../../scanner/useBarcodeScanner'
import { useTorch } from '../../scanner/useTorch'
import { manualRouteFor } from '../Manual/manualTarget'
import { useRecentPallets } from './useRecentPallets'
import styles from './PalletsScreen.module.css'

/** A pallet barcode, as the mock ERP and the spec both write it. */
const PALLET_CODE = /^PLT-[A-Za-z0-9-]+$/

export function PalletsScreen() {
  const navigate = useNavigate()
  const { show } = useToast()
  const { settings } = useDeviceSettings()
  const { pallets, isStale } = useRecentPallets()

  const onDetect = useCallback(
    (value: string) => {
      const code = value.trim().toUpperCase()

      if (!PALLET_CODE.test(code)) {
        // A serial scanned here is a wrong-screen mistake, not a bad label: say which.
        show('این بارکد پالت نیست. ابتدا بارکد پالت را اسکن کنید.', 'warn')
        return
      }

      navigate(palletPath(code))
    },
    [navigate, show],
  )

  const scanner = useBarcodeScanner({ enabled: true, onDetect })
  useTorch(scanner.track, settings.torch)

  return (
    <div className={styles.screen}>
      <CameraViewfinder
        size="tall"
        badgeLabel="دوربین فعال"
        hint="بارکد پالت را اسکن کنید"
        status={scanner.status}
        error={scanner.error}
        videoRef={scanner.videoRef}
        onActivate={scanner.retry}
      />

      {import.meta.env.DEV && <ScanSimulator onScan={onDetect} />}

      <div className={styles.listLabel}>
        پالت‌های اخیر
        {isStale && ' · ذخیره‌شده روی دستگاه'}
      </div>

      <div className={`${styles.list} scroll-y`}>
        {pallets.length === 0 ? (
          <div className={styles.empty}>پالتی ثبت نشده است.</div>
        ) : (
          pallets.map((pallet) => (
            <button
              type="button"
              key={pallet.palletCode}
              className={styles.recent}
              onClick={() => navigate(palletPath(pallet.palletCode))}
            >
              <span className={styles.code}>{pallet.palletCode}</span>
              {/* The artboard's own meta: the spaced ratio, or «تکمیل‌شده» once closed. */}
              <span className={styles.meta}>
                {pallet.status === 'closed'
                  ? 'تکمیل‌شده'
                  : faRatio(pallet.itemCount, pallet.capacityMax)}
              </span>
            </button>
          ))
        )}
      </div>

      <button
        type="button"
        className={styles.manual}
        onClick={() => {
          const route = manualRouteFor({ kind: 'pallet' })
          navigate(route.to, { state: route.state })
        }}
      >
        ورود دستی کد پالت
      </button>
    </div>
  )
}
